import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const M_LABEL = { CRYPTO: "ticker.crypto", INDICES: "ticker.indices", FX: "ticker.fx", COMMOD: "ticker.commodities" };

/**
 * MarketTicker — infinite-scrolling band of global market symbols
 * with mini sparklines. CSS keyframe animation; deterministic data
 * (no live feeds) — purely a "live system" visual cue.
 */
const SYMBOLS = [
    { s: "BTC/USD", p: "112,486", d: "+2.41%", u: true, m: "CRYPTO", spark: [60, 58, 62, 55, 50, 48, 44, 42, 38, 32, 30, 28, 24, 22, 18] },
    { s: "ETH/USD", p: "4,128", d: "+1.07%", u: true, m: "CRYPTO", spark: [55, 52, 50, 48, 50, 46, 44, 42, 40, 38, 36, 34, 30, 28, 26] },
    { s: "S&P 500", p: "5,914.32", d: "+0.34%", u: true, m: "INDICES", spark: [60, 56, 52, 50, 46, 48, 44, 40, 38, 36, 34, 32, 30, 28, 24] },
    { s: "NDX", p: "21,084.5", d: "+0.71%", u: true, m: "INDICES", spark: [50, 48, 50, 46, 44, 42, 40, 38, 36, 34, 30, 26, 24, 22, 18] },
    { s: "EUR/USD", p: "1.0834", d: "−0.18%", u: false, m: "FX", spark: [40, 42, 44, 46, 48, 46, 50, 48, 52, 50, 54, 52, 56, 58, 56] },
    { s: "GBP/JPY", p: "198.42", d: "−0.06%", u: false, m: "FX", spark: [40, 42, 40, 44, 42, 46, 44, 48, 46, 50, 48, 52, 50, 54, 52] },
    { s: "XAU/USD", p: "2,654.10", d: "+0.42%", u: true, m: "COMMOD", spark: [55, 50, 52, 48, 46, 44, 40, 38, 36, 32, 30, 28, 26, 24, 22] },
    { s: "WTI Oil", p: "73.12", d: "−1.24%", u: false, m: "COMMOD", spark: [30, 34, 32, 36, 38, 40, 42, 40, 44, 46, 48, 50, 48, 52, 50] },
    { s: "SOL/USD", p: "228.74", d: "+3.18%", u: true, m: "CRYPTO", spark: [55, 52, 50, 46, 44, 40, 38, 34, 30, 28, 24, 22, 20, 18, 14] },
    { s: "DXY", p: "104.56", d: "−0.09%", u: false, m: "FX", spark: [40, 42, 44, 42, 46, 44, 48, 46, 50, 48, 52, 50, 54, 52, 56] },
];

const Spark = ({ pts, up }) => {
    const path = useMemo(() => {
        if (!pts?.length) return "";
        const w = 60;
        const step = w / (pts.length - 1);
        return pts
            .map((y, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(2)},${y}`)
            .join(" ");
    }, [pts]);
    return (
        <svg viewBox="0 0 60 80" width="50" height="22" className="block">
            <path
                d={path}
                fill="none"
                stroke={up ? "#00E5FF" : "#A78BFA"}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.85"
            />
        </svg>
    );
};

const TickerItem = ({ item }) => {
    const { t } = useTranslation();
    return (
    <div className="flex items-center gap-3 px-6 shrink-0 border-r border-white/[0.06] h-full">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 w-16">
            {M_LABEL[item.m] ? t(M_LABEL[item.m]) : item.m}
        </span>
        <span className="font-cabinet text-white text-[14px] tracking-tight">{item.s}</span>
        <span className="font-mono text-[12px] text-white/85">{item.p}</span>
        <span
            className={`font-mono text-[11px] tracking-tight ${
                item.u ? "text-cyan" : "text-violet-soft"
            }`}
        >
            {item.d}
        </span>
        <Spark pts={item.spark} up={item.u} />
    </div>
    );
};

export const MarketTicker = ({ className = "", height = 56 }) => {
    return (
        <div
            className={`relative overflow-hidden border-y border-white/[0.06] bg-graphite/80 backdrop-blur-md ${className}`}
            style={{ height }}
            data-testid="market-ticker"
        >
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-r from-black to-transparent" />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-l from-black to-transparent" />
            <div className="flex items-center h-full ticker-track">
                {[...SYMBOLS, ...SYMBOLS].map((item, i) => (
                    <TickerItem item={item} key={`${item.s}-${i}`} />
                ))}
            </div>
            <style>{`
                .ticker-track {
                    width: max-content;
                    animation: ticker-scroll 70s linear infinite;
                }
                @keyframes ticker-scroll {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                }
                .ticker-track:hover {
                    animation-play-state: paused;
                }
            `}</style>
        </div>
    );
};

export default MarketTicker;
